import { captureException } from '@sentry/react'
import { Form, Formik, FormikHelpers } from 'formik'
import { useEffect } from 'react'
import { useToasts } from '~/context/toasts'
import { PageBySlugQuery } from '~/graphql/page-by-slug'
import { CustomFieldsSection } from '~/components/custom-fields/CustomFieldsSection'
import { useRecaptcha } from '~/hooks/use-recaptcha'
import { DonationRecurrenceInterval, OnlinePaymentMethodType } from '~/types/graphql'
import { FocusError } from '~/utils/focus-error'

import { BillingDetails } from './components/billing-details'
import { Dedication } from './components/dedication'
import { DonorInformation } from './components/donor-information'
import { Loading } from './components/loading'
import { Summary } from './components/summary'
import { useGetOrderFeesQuery } from './graphql/get-order-fees'
import { useCreateOrder } from './hooks/use-create-order'
import { useForm, Values } from './hooks/use-form'
import { usePaymentRequest } from './hooks/use-payment-request'
import { usePayPledge } from '~/pages/event-pledge-form/hooks/use-pay-pledge'
import { PaymentMethod } from './types'

export type PaymentSubmitMode =
  | { kind: 'createOrder' }
  | {
      kind: 'payPledge'
      pledgeWebId: string
      amount?: number
      recurrenceInterval?: DonationRecurrenceInterval
    }

type Props = {
  page: PageBySlugQuery['pageBySlug']
  onSuccess: (amount: number, orderId: string) => void
  fundraiserId?: string
  submitMode?: PaymentSubmitMode
}
export type OrderFeesInput = {
  amount: number
  pageId: string
  paymentMethod: OnlinePaymentMethodType
  tip: number
}
export type FeesProps = { variable: number; fixed: number }

export const KindestPaymentForm = ({
  page,
  onSuccess,
  fundraiserId,
  submitMode = { kind: 'createOrder' },
}: Props) => {
  const {
    id,
    allocations,
    nonprofit,
    defaultDonorCoverFeesValue,
    pageFeatures,
  } = page
  const toasts = useToasts()
  const recaptcha = useRecaptcha()
  const storage = JSON.parse(sessionStorage.formData)

  const { data, loading, fetchMore } = useGetOrderFeesQuery({
    variables: {
      input: {
        amount: storage.amount,
        pageId: id,
        paymentMethod: OnlinePaymentMethodType.Card,
        tip: 0,
      },
    },
  })

  // PayPledgeInput carries no customFieldValues, so a pledge asks nothing.
  const customFields = submitMode.kind === 'payPledge' ? [] : page.orderCustomFields

  // INFO: temporary workaround - propagate to BE
  const { initialValues, schema } = useForm(
    pageFeatures.donorCoversFees && defaultDonorCoverFeesValue,
    pageFeatures.addressForm,
    customFields,
    nonprofit.defaultAllocation?.id
  )

  const createOrder = useCreateOrder({
    pageId: id,
    onSuccess,
    fundraiserId,
    nonprofit,
  })

  const payPledge = usePayPledge({
    pledgeWebId: submitMode.kind === 'payPledge' ? submitMode.pledgeWebId : '',
    amountOverride: submitMode.kind === 'payPledge' ? submitMode.amount : undefined,
    recurrenceOverride: submitMode.kind === 'payPledge' ? submitMode.recurrenceInterval : undefined,
    onSuccess,
  })

  const submit = submitMode.kind === 'payPledge' ? payPledge : createOrder

  const handleSubmit = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    const { setSubmitting } = helpers

    try {
      const recaptchaResponse = await recaptcha.execute()
      await submit(recaptchaResponse, values)
    } catch (error: any) {
      toasts.error(error.message)
      captureException(error)
      recaptcha.reset()
      setSubmitting(false)
    }
  }

  const handlePaymentMethod = (values: Values) => async () => {
    try {
      const recaptchaResponse = await recaptcha.execute()
      await submit(recaptchaResponse, values)
    } catch (error: any) {
      toasts.error(error.message)
      captureException(error)
      recaptcha.reset()
    }
  }

  return (
    <>
      {!loading && data ? (
        <Formik
          initialValues={{
            ...initialValues,
            ...storage,
            coverProcessingFees:
              pageFeatures.donorCoversFees &&
              (storage.coverProcessingFees ?? initialValues.coverProcessingFees),
            orderCalculation: data.orderCalculation,
            allocationId: nonprofit.defaultAllocation?.id ?? '',
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => {
            const getNewAmount = async (selectedMethod: PaymentMethod) => {
              const paymentMethod =
                selectedMethod == PaymentMethod.Ach
                  ? OnlinePaymentMethodType.BankAccount
                  : OnlinePaymentMethodType.Card
              const { data } = await fetchMore({
                variables: {
                  input: {
                    amount: storage.amount,
                    pageId: id,
                    paymentMethod: paymentMethod,
                    tip: 0,
                  },
                },
              })
              setFieldValue('orderCalculation', data.orderCalculation)
            }

            // eslint-disable-next-line react-hooks/rules-of-hooks
            const { paymentRequest } = usePaymentRequest({
              values,
              onPaymentMethod: handlePaymentMethod(values),
            })

            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
              if (values.paymentMethod == PaymentMethod.Other) {
                recaptcha.execute()
              }
            }, [values.paymentMethod])

            return (
              <>
                <FocusError />
                <Form>
                  <DonorInformation
                    allocations={allocations}
                    nonprofit={nonprofit}
                    features={pageFeatures}
                    smsMarketingEnabled={page.smsMarketingEnabled}
                    isEvent={page.type === 'event'}
                    className="!border-0 !pb-0 mb-4"
                  />
                  {page.type !== 'event' && <Dedication />}

                  {customFields.length > 0 && (
                    <div className="px-5 pb-5 md:px-10 border-b border-[#CFD8E5] mt-4">
                      <CustomFieldsSection fields={customFields} />
                    </div>
                  )}
                  <BillingDetails
                    donorCoversFees={pageFeatures.donorCoversFees}
                    nonprofit={nonprofit}
                    onPaymentMethodChange={paymentMethod =>
                      getNewAmount(paymentMethod)
                    }
                  />
                  <Summary
                    paymentMethod={values.paymentMethod}
                    paymentRequest={paymentRequest}
                  />
                </Form>
              </>
            )
          }}
        </Formik>
      ) : (
        <Loading />
      )}
    </>
  )
}
